import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Orbit, Star } from 'lucide-react'
import Header from '@components/layout/Header'
import Footer from '@components/layout/Footer'
import BackgroundLayer from '@components/layout/Background/BackgroundLayer'
import MobileBottomNav from '@components/layout/MobileBottomNav'
import PublicPageHeader from '@components/common/PublicPageHeader'
import DatePicker from '@components/common/DatePicker'
import { useAuthStore } from '@store/authStore'
import { useSettingsStore } from '@store/settingsStore'
import {
  SunPlanetIcon,
  MoonPlanetIcon,
  MarsPlanetIcon,
  MercuryPlanetIcon,
  JupiterPlanetIcon,
  VenusPlanetIcon,
  SaturnPlanetIcon,
  RahuPlanetIcon,
  KetuPlanetIcon,
} from '../free-kundli/components/AstroIcons'
import { TransitEvent, transitService, getTransitProgress } from '@services/transitService'
import toast from 'react-hot-toast'
import { TRANSIT_TRANSLATIONS } from '@/languages/transit'

const RANGE_PRESET_KEYS = [
  { key: '30', days: 30 },
  { key: '60', days: 60 },
  { key: '90', days: 90 },
  { key: '365', days: 365 },
  { key: 'custom', days: null },
] as const

type RangePreset = (typeof RANGE_PRESET_KEYS)[number]['key']
type SupportedLanguage = 'english' | 'hindi'

const clampDateRange = (startDate: string, endDate: string): { startDate: string; endDate: string } => {
  if (new Date(endDate) < new Date(startDate)) {
    return { startDate, endDate: startDate }
  }
  return { startDate, endDate }
}

const formatDisplayDate = (value: string, language: SupportedLanguage, includeTime = false): string => {
  const date = new Date(value)
  return date.toLocaleString(language === 'hindi' ? 'hi-IN' : 'en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  })
}

const getPlanetIcon = (planet: string, className = 'w-6 h-6') => {
  switch (planet.toLowerCase()) {
    case 'sun':
      return <SunPlanetIcon className={className} />
    case 'moon':
      return <MoonPlanetIcon className={className} />
    case 'mars':
      return <MarsPlanetIcon className={className} />
    case 'mercury':
      return <MercuryPlanetIcon className={className} />
    case 'jupiter':
      return <JupiterPlanetIcon className={className} />
    case 'venus':
      return <VenusPlanetIcon className={className} />
    case 'saturn':
      return <SaturnPlanetIcon className={className} />
    case 'rahu':
      return <RahuPlanetIcon className={className} />
    case 'ketu':
      return <KetuPlanetIcon className={className} />
    default:
      return <Star className={`${className} text-purple-400`} />
  }
}

const TransitCard = ({ transit, language }: { transit: TransitEvent; language: SupportedLanguage }) => {
  const progress = getTransitProgress(transit)
  const t = language === 'hindi' ? TRANSIT_TRANSLATIONS.Hindi : TRANSIT_TRANSLATIONS.English

  return (
    <article className="bg-white/[0.08] border border-white/20 rounded-3xl p-5 md:p-6 shadow-xl hover:bg-white/[0.12] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-2xl font-bold text-white font-sans">{transit.display.planet}</h3>
            {transit.retrograde && (
              <span className="inline-flex items-center rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 px-2 py-0.5 text-xs font-bold">
                {t.retrogradeBadge}
              </span>
            )}
          </div>
          <p className="mt-2 text-purple-200/80 font-sans text-base">
            {transit.display.rashi} <span className="mx-2 text-purple-300/40">•</span> {transit.display.nakshatra}
          </p>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-white/[0.08] border border-white/20 flex items-center justify-center shrink-0">
          {getPlanetIcon(transit.planetKey, 'w-8 h-8')}
        </div>
      </div>

      <div className="mt-7 grid grid-cols-2 gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-purple-300/60 font-bold font-sans">{t.starts}</p>
          <p className="text-xl font-extrabold text-white mt-1 font-sans">{formatDisplayDate(transit.startsAt, language)}</p>
          <p className="text-sm text-purple-200/70 mt-1 font-sans">{formatDisplayDate(transit.startsAt, language, true).split(', ')[1]}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-[0.14em] text-purple-300/60 font-bold font-sans">{t.ends}</p>
          <p className="text-xl font-extrabold text-white mt-1 font-sans">{formatDisplayDate(transit.endsAt, language)}</p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-sm">
          <p className="text-[11px] uppercase tracking-[0.14em] text-purple-300/60 font-bold font-sans">{t.transitProgress}</p>
          <p className="text-white font-bold font-sans">{progress}%</p>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-400 transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </article>
  )
}

const TransitSearch = () => {
  const { lang } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { contentLanguage, setContentLanguage } = useSettingsStore()

  const [language, setLanguage] = useState<SupportedLanguage>(() => {
    if (lang === 'hindi' || lang === 'english') return lang
    if (user?.content_language?.toLowerCase() === 'hindi') return 'hindi'
    return contentLanguage
  })

  const defaultRange = useMemo(() => transitService.getRangeForDays(30), [])
  const t = language === 'hindi' ? TRANSIT_TRANSLATIONS.Hindi : TRANSIT_TRANSLATIONS.English

  const [activePreset, setActivePreset] = useState<RangePreset>('30')
  const [startDate, setStartDate] = useState(defaultRange.startDate)
  const [endDate, setEndDate] = useState(defaultRange.endDate)
  const [loading, setLoading] = useState(false)
  const [transits, setTransits] = useState<TransitEvent[]>([])

  // Sync language from Zustand/user preference changes
  useEffect(() => {
    const storeLang: SupportedLanguage = user?.content_language?.toLowerCase() === 'hindi' ? 'hindi' : contentLanguage
    setLanguage((prev) => (prev === storeLang ? prev : storeLang))
  }, [contentLanguage, user?.content_language])

  // Sync language from URL changes (deep links / back-forward navigation)
  useEffect(() => {
    if (lang === 'hindi' || lang === 'english') {
      setLanguage((prev) => (prev === lang ? prev : lang))
    }
  }, [lang])

  // Keep URL and store in sync when language state changes
  useEffect(() => {
    const routeLang = lang === 'hindi' || lang === 'english' ? lang : null
    if (routeLang !== language) {
      navigate(`/transit-search/${language}`, { replace: true })
    }
    setContentLanguage(language)
  }, [language, lang, navigate, setContentLanguage])

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      const safeRange = clampDateRange(startDate, endDate)
      try {
        const data = await transitService.getTransits({ ...safeRange, language })
        setTransits(data)
      } catch (error) {
        const loadErrorMessage =
          language === 'hindi' ? TRANSIT_TRANSLATIONS.Hindi.loadError : TRANSIT_TRANSLATIONS.English.loadError
        toast.error(loadErrorMessage)
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [startDate, endDate, language])

  useEffect(() => {
    if (new Date(endDate) < new Date(startDate)) {
      setEndDate(startDate)
    }
  }, [startDate, endDate])

  const handlePresetClick = (preset: RangePreset) => {
    setActivePreset(preset)
    if (preset === 'custom') return

    const selected = RANGE_PRESET_KEYS.find((item) => item.key === preset)
    if (!selected?.days) return

    const range = transitService.getRangeForDays(selected.days)
    setStartDate(range.startDate)
    setEndDate(range.endDate)
  }

  return (
    <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-purple-100 relative pb-16 md:pb-0">
      <BackgroundLayer />
      <Header />

      <main className="relative z-10 pt-14 md:pt-16 pb-16 md:pb-24 px-4 sm:px-6">
        <div className="container mx-auto max-w-5xl">
          <PublicPageHeader
            title={t.title}
            highlightedText={t.highlightedText}
            description={t.description}
            icon={Orbit}
          />

          <section className="bg-white/[0.08] border border-white/20 rounded-3xl p-4 md:p-6 shadow-xl">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap md:justify-center gap-2">
              {RANGE_PRESET_KEYS.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  className={`w-full md:w-auto min-h-[44px] px-4 py-2.5 rounded-2xl text-sm font-bold text-center leading-tight border transition-colors ${
                    preset.key === 'custom'
                      ? 'col-span-2 sm:col-span-1 justify-self-center max-w-[calc(50%-0.25rem)] sm:max-w-none'
                      : ''
                  } ${
                    activePreset === preset.key
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-transparent'
                      : 'bg-white/[0.06] text-purple-200 border-white/20 hover:bg-white/[0.12]'
                  }`}
                >
                  {t.rangeLabels[preset.key]}
                </button>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <DatePicker
                value={startDate}
                onChange={(date) => {
                  setActivePreset('custom')
                  setStartDate(date)
                }}
                placeholder={t.starts}
                className="w-full"
              />
              <p className="text-center text-purple-200/70 font-medium">{t.to}</p>
              <DatePicker
                value={endDate}
                onChange={(date) => {
                  setActivePreset('custom')
                  setEndDate(date)
                }}
                placeholder={t.ends}
                className="w-full"
              />
            </div>
          </section>

          <section className="mt-7 space-y-5">
            {loading ? (
              <div className="text-center py-16 text-purple-300 animate-pulse">{t.loading}</div>
            ) : transits.length === 0 ? (
              <div className="bg-white/[0.08] border border-white/20 rounded-3xl p-10 text-center text-purple-200/70">
                {t.empty}
              </div>
            ) : (
              transits.map((transit) => <TransitCard key={transit.id} transit={transit} language={language} />)
            )}
          </section>
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  )
}

export default TransitSearch
